
import React, { useEffect, useCallback, useState } from "react";
import { BarChart3, RefreshCw, Clock, Zap } from "lucide-react";

import Card from "../common/Card";
import { ActionButton } from "../ui/ActionButton";
import Toast from "../common/Toast";

import { EmptyState } from "./EmptyStates";
import { ActivityTable } from "./ActivityTable";
import { ActivityLoadingState, ActivityErrorState } from "./ActivityStates";

import useActivityStore from "../hooks/useActivity";

export const ActivityReport = ({ autoRefresh = false }) => {
  const [toast, setToast] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastFetch, setLastFetch] = useState(null);
  
  const { activities, loading, error, fetchActivities } = useActivityStore();

  useEffect(() => {
    fetchActivities();
    setLastFetch(new Date());
  }, [fetchActivities]);

  useEffect(() => {
    if (autoRefresh && !loading && !error) {
      const interval = setInterval(() => {
        fetchActivities();
        setLastFetch(new Date());
      }, 30000); 

      return () => clearInterval(interval);
    }
  }, [autoRefresh, loading, error, fetchActivities]);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await fetchActivities();
      setLastFetch(new Date());
      setToast({ message: `Refreshed ${activities?.length || 0} activities`, type: "success" });
    } catch (error) {
      setToast({ message: `Failed to refresh activities`, type: "error" });
    } finally {
      setIsRefreshing(false);
    }
  }, [fetchActivities, activities]);

  const handleRetry = useCallback(() => {
    fetchActivities();
    setLastFetch(new Date());
  }, [fetchActivities]);

  const isEmpty = !loading && !error && (!activities || activities.length === 0);
  const hasData = !loading && !error && activities && activities.length > 0;
  const isStale = lastFetch && (Date.now() - lastFetch.getTime()) > 30000;

  const renderContent = () => {
    if (loading) {
      return <ActivityLoadingState />;
    }

    if (error) {
      return <ActivityErrorState message={error} onRetry={handleRetry} />;
    }

    if (isEmpty) {
      return (
        <EmptyState
          icon={BarChart3}
          title="No Activity Found"
          description="There are no recent activities to display."
          gradient="from-indigo-600 to-purple-600"
        />
      );
    }

    if (hasData) {
      return <ActivityTable reportRows={activities} />;
    }

    return null;
  };

  const renderStatusIndicator = () => {
    if (isRefreshing) {
      return (
        <div className="flex items-center gap-2 text-blue-600">
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span className="text-sm">Refreshing...</span>
        </div>
      );
    }

    if (isStale) {
      return (
        <div className="flex items-center gap-2 text-amber-600">
          <Clock className="w-4 h-4" />
          <span className="text-sm">Data may be stale</span>
        </div>
      );
    }

    if (autoRefresh) {
      return (
        <div className="flex items-center gap-2 text-green-600">
          <Zap className="w-4 h-4" />
          <span className="text-sm">Auto-refreshing</span>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      <Card
        variant="gradient"
        title="Activity Report"
        subtitle="Monitor system activities and user actions"
        icon={BarChart3}
        gradient="from-indigo-600 to-purple-600"
        className="mb-8"
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent mb-1">
              Recent Activities
            </h3>
            <div className="flex items-center gap-4">
              <p className="text-slate-600 text-sm">
                Latest system activities and user actions
              </p>
              {renderStatusIndicator()}
            </div>
          </div>

          {!loading && !error && (
            <div className="flex items-center gap-3">
              <ActionButton
                onClick={handleRefresh}
                variant="secondary"
                disabled={isRefreshing}
                className="text-xs"
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${isRefreshing ? 'animate-spin' : ''}`} />
                {isRefreshing ? 'Refreshing...' : 'Refresh'}
              </ActionButton>
            </div>
          )}
        </div>

        {renderContent()}

        {hasData && (
          <div className="mt-6 pt-6 border-t border-slate-200">
            <div className="flex items-center justify-between text-sm text-slate-500">
              <span>Showing {activities.length} recent activities</span>
              <div className="flex items-center gap-4">
                {lastFetch && (
                  <span>Last updated: {lastFetch.toLocaleTimeString()}</span>
                )}
                {autoRefresh && (
                  <span className="text-green-600">Auto-refresh enabled</span>
                )}
              </div>
            </div>
          </div>
        )}
      </Card>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};